import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { corePayorReportClient } from 'app/api/apiClient/generated/corePayorReportClient';
import { ProfileSettingValueDTO } from 'app/api/apiClient/generated/types';
import { queries } from 'app/constants/queries';
import { PayorProfileType } from 'app/constants/user';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { indexBy } from 'app/utils/lib/array';
import { handleSuccess } from 'app/utils/lib/context';

// Local Variables
const profileSettingIds: PayorProfileType[] = [
  PayorProfileType.DuplicateInvoiceMonitoring,
  PayorProfileType.EnableCreditNotes,
  PayorProfileType.EnableLoads,
  PayorProfileType.EnablePayorInvoiceReview,
  PayorProfileType.ManualUpload,
  PayorProfileType.NewRelationshipQuickPayRestrictionSpCount,
  PayorProfileType.PayeeQuickPayDefault,
  PayorProfileType.QuickPayRevenueShareCalculatedWeekly,
  PayorProfileType.QuickPayRevenueShareThreshold,
];

export const useGetTerms = () =>
  useQueryEnhanced([queries.payorTerms],
    corePayorReportClient.listTerms);

export const useGetCurrentSettlement = (settlementStatementTypeId: number[], options?: { enabled?: boolean }) =>
  useQueryEnhanced([queries.payorCurrentSettlement, settlementStatementTypeId],
    () => corePayorReportClient.settlementStatements({
      endDate: undefined,
      exportReport: false,
      page: 0,
      pageSize: 1,
      settlementStatementTypeId,
      sortAsc: undefined,
      sortField: undefined,
      startDate: undefined,
    }),
    options);

export const useGetCreditUsage = () =>
  useQueryEnhanced([queries.payorCreditUsage],
    corePayorReportClient.creditUsage);

export const useGetProfileSettings = () => {
  const queryFn = async (): Promise<AxiosResponse<Record<string, ProfileSettingValueDTO>>> => {
    const result = await corePayorReportClient.profileSettings({ ids: profileSettingIds });

    return {
      ...result,
      data: indexBy(result.data.profileSettings, setting => setting.id.toString()),
    };
  };

  return useQueryEnhanced([queries.payorProfileSettings, profileSettingIds], queryFn);
};

export const useReleasePayorInvoiceBatchReview = () => {
  const alertsDispatch = useAlertsDispatch();

  const mutationFn = ({
    batchGuid,
  }: {
    batchGuid: string;
    batchId: string; // only used for alert message
  }) => corePayorReportClient.releaseInvoiceBatchReview(batchGuid);

  return useMutationEnhanced(mutationFn, {
    invalidateTableQueryPrefixes: [queries.payorInvoiceBatchReviews],
    onSuccess: (_, payload) => {
      handleSuccess({
        dispatch: alertsDispatch,
        successMessage: `Payments released successfully for batch ${payload.batchId}!`,
      });
    },
  });
};

export const useRemovePayorInvoiceBatchReview = () => {
  const alertsDispatch = useAlertsDispatch();

  const mutationFn = ({
    batchGuid,
  }: {
    batchGuid: string;
    batchId: string; // only used for alert message
  }) => corePayorReportClient.removeInvoiceBatchReview(batchGuid);

  return useMutationEnhanced(mutationFn, {
    invalidateTableQueryPrefixes: [queries.payorInvoiceBatchReviews],
    onSuccess: (_, payload) => {
      handleSuccess({
        dispatch: alertsDispatch,
        successMessage: `Batch ${payload.batchId} has been removed!`,
      });
    },
  });
};

export const useUpdateQuickPaySettings = () => {
  const queryClient = useQueryClient();
  const alertsDispatch = useAlertsDispatch();

  return useMutationEnhanced(corePayorReportClient.updateQuickpaySettings,
    {
      onSuccess: data => {
        handleSuccess({
          dispatch: alertsDispatch,
          successMessage: 'QuickPay setting for new carriers has been updated',
        });

        queryClient.setQueryData<Record<number, ProfileSettingValueDTO>>(
          [queries.payorProfileSettings, profileSettingIds],
          oldData => ({
            ...oldData,
            [PayorProfileType.PayeeQuickPayDefault]: data.payeeQuickpaySetting,
            [PayorProfileType.NewRelationshipQuickPayRestrictionSpCount]: data.standardPayInvoiceCountSetting,
          }),
        );
      },
    });
};
